import {useMutation, type UseMutationResult} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'

import type {CopyProjectOwner} from './types'

interface UseCopyProjectOptions {
  sourceOwnerLogin: string
  sourceOwnerIsOrganization: boolean
  sourceProjectNumber: number
}

export interface CopyProjectVariables {
  targetOwner: CopyProjectOwner
  /** Display title for the new project. Trimmed server-side. */
  title: string
  mode: 'copy' | 'template'
  /**
   * Only meaningful when `mode === 'copy'`; the dialog hides the checkbox in
   * template mode.
   */
  includeDraftIssues: boolean
  /** Optional abort signal so the caller can cancel the in-flight request. */
  signal?: AbortSignal
}

export interface CopyProjectResponse {
  htmlUrl: string
  number: number
  ownerLogin: string
}

/**
 * No cache invalidation: the new project belongs to a *different* repo (or to
 * the viewer's own account), so it never appears in the source repo's projects
 * list. The next render of the destination page reads it fresh.
 */
export function useCopyProject({
  sourceOwnerLogin,
  sourceOwnerIsOrganization,
  sourceProjectNumber,
}: UseCopyProjectOptions): UseMutationResult<CopyProjectResponse, Error, CopyProjectVariables> {
  return useMutation<CopyProjectResponse, Error, CopyProjectVariables>({
    mutationFn: async ({targetOwner, title, mode, includeDraftIssues, signal}) => {
      const ownerSegment = sourceOwnerIsOrganization ? 'orgs' : 'users'
      const url = `/${ownerSegment}/${encodeURIComponent(sourceOwnerLogin)}/projects/beta/${sourceProjectNumber}/copy`

      const body = new URLSearchParams()
      body.append('owner', targetOwner.login)
      body.append('memex_project_copy[title]', title)
      if (mode === 'template') {
        body.append('memex_project_copy[is_template]', 'true')
      }
      if (includeDraftIssues) {
        body.append('memex_project_copy[include_draft_issues]', 'on')
      }

      const response = await reactFetch(url, {
        method: 'POST',
        body,
        headers: {Accept: 'application/json'},
        signal,
      })

      if (response.ok) {
        return (await response.json()) as CopyProjectResponse
      }

      // Surface the server's first error string when available; fall back to a
      // generic message for permission failures (empty body) and other
      // non-JSON responses.
      let message = 'Failed to copy project. Please try again.'
      try {
        const payload = (await response.json()) as {errors?: string[]} | null
        const firstError = payload?.errors?.[0]
        if (firstError) {
          message = firstError
        }
      } catch {
        // body wasn't JSON — keep the generic message.
      }
      throw new Error(message)
    },
  })
}
